import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportsTaxSummaryData } from "src/features/Reports/types/report.types";
import { REPORTS_TAX_SUMMARY } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { restaurantSegment } from "src/utils/restaurantUrl";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";

export default function TaxSummaryReportPage() {
  const { hasRestaurant } = useAuth();
  const isOwnerAtStore = hasRestaurant?.storeStaffRole === "OWNER";
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const input = useMemo(
    () =>
      hasRestaurant?.correlationId && isOwnerAtStore
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, isOwnerAtStore, range.from, range.to]
  );
  const { data, loading, error } = useReportQuery<ReportsTaxSummaryData>(
    REPORTS_TAX_SUMMARY,
    "reportsTaxSummary",
    input
  );

  if (hasRestaurant?.correlationId && !isOwnerAtStore) {
    return (
      <Navigate
        to={`/restaurants/${restaurantSegment(hasRestaurant.correlationId, hasRestaurant?.name)}/reports/overview`}
        replace
      />
    );
  }

  const rows = data?.rows ?? [];

  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      error={error}
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr>
                  <th className={`pl-4 pr-2 ${headerCellClass}`}>Tax Rate</th>
                  <th className={`px-2 ${headerCellClass}`}>Taxable Sales</th>
                  <th className={`px-2 ${headerCellClass}`}>Tax Collected</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-[#6b7280] text-sm">
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={i}
                      className="border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB]"
                    >
                      <td className="py-2 pl-4 pr-2">{row.taxRate != null ? `${(row.taxRate * 100).toFixed(2)}%` : "—"}</td>
                      <td className="py-2 px-2">{formatCentsToDollarsWithDollarSign(row.taxableSalesCents ?? 0)}</td>
                      <td className="py-2 px-2">{formatCentsToDollarsWithDollarSign(row.taxCollectedCents ?? 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportPageLayout>
  );
}
